// =============================================================================
// InspectionRegisterScreen — /inspections (every Coretel employee)
//
// ONE register, the same for everyone. 12 Oct 2026 — James, from a site at
// Mount Tamborine with a client field manager asking for records: "ALL
// employees can see and check the inspection register on ALL rigging
// equipment as this is a client requirement and they will shut us down if a
// guy can't show the inspection on a block on site just because it's not
// allocated to him." So the old field mode (table limited to your own
// allocation, titled "My equipment") is gone. Field, supervisor, manager and
// director all see the full audit register; a "Mine" chip narrows it.
//
// Reads v_inspection_register only and writes nothing. Rows link to the item
// page (/inspections/equipment/:kind/:itemId) which holds the history, the
// PDF per inspection and Log inspection. "Add equipment" opens the create
// screens under /inspections/add/* so crews can reach them (the /assets copies
// live in the HR hub, which field logins don't hold). Allocation stays on
// /assets — managers and supervisors only (James, 12 Oct).
//
// Kiosk (crew tablet, mig 410) can read everything here and write nothing.
// Subcontractors are bounced at the route.
//
// Export (piece 2, 12 Oct 2026): Excel or PDF of whatever is on screen —
// James: "a clean report I can send the client, not all equipment, just the
// stuff they have on site." Scope it three ways, all stackable: the filter
// strip (kit / colour / status / search), a PROJECT CREW chip (everyone with
// an open roster stint on that project → the gear allocated to them), and
// tick boxes on the rows. Ticked rows win when any are ticked. The export
// module (inspectionRegisterExport.ts, exceljs + pdfmake) is lazy-loaded on
// the button press so it stays out of the main bundle. Allocation quality is
// what makes "on site" true — piece 3 fixes that model.
// =============================================================================

import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  HardHat, Wrench, Search, Plus, Filter, ChevronRight, ShieldCheck, Zap, PackagePlus,
  Download, FileSpreadsheet, FileText, X, CheckSquare, Square,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Card, Pill, Spinner, EmptyState, Field, Input } from "@/components/ui";

interface RegisterRow {
  kind:                     "rigging" | "power_tool";
  item_id:                  string;
  code:                     string;
  asset_tag:                string;
  display_name:             string | null;
  item_class:               string | null;
  make:                     string | null;
  model:                    string | null;
  serial_number:            string | null;
  swl_kg:                   number | null;
  kit_name:                 string | null;
  assigned_to_employee_id:  string | null;
  assigned_to_name:         string | null;
  status:                   string | null;
  tag_colour:               string | null;
  tag_number:               string | null;
  last_inspection_date:     string | null;
  next_inspection_date:     string | null;
  last_result:              string | null;
  last_inspector_name:      string | null;
  urgency:                  string | null;
}

type Kind = "rigging" | "power_tool";

const URGENCY_TONE: Record<string, "danger" | "warning" | "approved" | "default"> = {
  overdue:          "danger",
  never_inspected:  "warning",
  due_soon:         "warning",
  current:          "approved",
  retired:          "default",
};

const URGENCY_LABEL: Record<string, string> = {
  overdue:          "Overdue",
  never_inspected:  "Never inspected",
  due_soon:         "Due soon",
  current:          "Current",
  retired:          "Retired",
};

export default function InspectionRegisterScreen() {
  const user = useRequireAuth();

  const [ticked,        setTicked]        = useState<Set<string>>(new Set()); // item_ids
  // Tab — rigging vs test & tag. URL param so refresh keeps the tab.
  const [params, setParams] = useSearchParams();
  const tab = (params.get("kind") as Kind | null) ?? "rigging";
  const setTab = (k: Kind) => {
    const next = new URLSearchParams(params);
    next.set("kind", k);
    setParams(next, { replace: true });
    setTicked(new Set());
  };

  const [search,        setSearch]        = useState("");
  const [scope,         setScope]         = useState<"all" | "mine">("all");
  const [projectId,     setProjectId]     = useState<string>("");          // project-crew scope
  const [exportOpen,    setExportOpen]    = useState(false);
  const [urgencyFilter, setUrgencyFilter] = useState<"all" | "overdue" | "due_soon" | "current" | "never_inspected">("all");
  const [kitFilter,     setKitFilter]     = useState<string>("all");
  const [personFilter,  setPersonFilter]  = useState<string>("all");    // assigned_to_name
  const [colourFilter,  setColourFilter]  = useState<"all" | "Red" | "Green" | "Blue" | "Yellow">("all");

  // The whole register for the tab, for everyone. RLS on the underlying
  // tables is SELECT true for authenticated — the old "mine only" filter was
  // a screen choice, not a database rule.
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["inspection_register", tab],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_inspection_register")
        .select("*")
        .eq("kind", tab)
        .order("urgency")
        .order("next_inspection_date", { nullsFirst: true });
      if (error) throw error;
      return (data ?? []) as RegisterRow[];
    },
  });

  // Projects a crew could be on right now — for the "project crew" scope.
  const { data: projects = [] } = useQuery({
    queryKey: ["inspection_register_projects"],
    enabled: !!user && user.role !== "subcontractor",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name, status")
        .in("status", ["scheduled", "mobilising", "live", "on_hold", "acceptance_pending"])
        .order("project_code");
      if (error) throw error;
      return (data ?? []) as { project_id: string; project_code: string; project_name: string; status: string }[];
    },
  });

  // Who is on that project: open roster stints (on_site_to null or not yet
  // ended). The gear allocated to those people is "on site" as far as the
  // register can tell — see the header note on allocation quality.
  const { data: crewIds = null } = useQuery({
    queryKey: ["inspection_register_crew", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const today = localDayKey();
      const { data, error } = await (supabase as any)
        .from("site_roster")
        .select("employee_id, on_site_to")
        .eq("project_id", projectId)
        .or(`on_site_to.is.null,on_site_to.gte.${today}`);
      if (error) throw error;
      return new Set(((data ?? []) as { employee_id: string }[]).map(r => r.employee_id));
    },
  });

  const isKiosk   = user?.role === "kiosk";
  const myEmpId   = user?.employee_id ?? null;
  const canScope  = !!myEmpId && !isKiosk;

  const scoped = useMemo(() => {
    let out = rows;
    if (scope === "mine" && myEmpId) out = out.filter(r => r.assigned_to_employee_id === myEmpId);
    if (projectId && crewIds) out = out.filter(r => !!r.assigned_to_employee_id && crewIds.has(r.assigned_to_employee_id));
    return out;
  }, [rows, scope, myEmpId, projectId, crewIds]);

  const kits = useMemo(() => {
    const set = new Set<string>();
    for (const r of scoped) if (r.kit_name) set.add(r.kit_name);
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [scoped]);

  const people = useMemo(() => {
    const set = new Set<string>();
    for (const r of scoped) if (r.assigned_to_name) set.add(r.assigned_to_name);
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [scoped]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scoped.filter(r => {
      if (urgencyFilter !== "all" && r.urgency !== urgencyFilter) return false;
      if (kitFilter !== "all" && (kitFilter === "__pool__" ? !!r.kit_name : r.kit_name !== kitFilter)) return false;
      if (personFilter !== "all" && (personFilter === "__unassigned__" ? !!r.assigned_to_name : r.assigned_to_name !== personFilter)) return false;
      if (colourFilter !== "all" && (r.tag_colour ?? "") !== colourFilter) return false;
      if (q) {
        // Serial number is in here on purpose — on site the S/N stamped on the
        // gear is usually the only thing the person holding it can read off.
        const hay = [
          r.code, r.asset_tag, r.display_name, r.item_class, r.kit_name, r.assigned_to_name,
          r.serial_number, r.make, r.model, r.tag_number,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [scoped, urgencyFilter, kitFilter, personFilter, colourFilter, search]);

  const counts = useMemo(() => ({
    total:           scoped.length,
    overdue:         scoped.filter(r => r.urgency === "overdue").length,
    dueSoon:         scoped.filter(r => r.urgency === "due_soon").length,
    neverInspected:  scoped.filter(r => r.urgency === "never_inspected").length,
    current:         scoped.filter(r => r.urgency === "current").length,
  }), [scoped]);

  // Subcontractor block (defensive — the route also bounces them). Every
  // hook is above this line on purpose (blank-screen rule).
  if (user?.role === "subcontractor") {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><div className="p-6 text-text-muted text-sm">
          Inspection register is restricted to Coretel employees.
        </div></Card>
      </div>
    );
  }

  const addHref = tab === "rigging" ? "/inspections/add/rigging" : "/inspections/add/power-tool";
  const project = projects.find(p => p.project_id === projectId) ?? null;

  // What the export gets: every ticked row on this tab (ticks survive filter
  // changes, so you can filter by one kit, tick, filter by another, tick), or
  // when nothing is ticked, everything currently shown.
  const tickedVisible = filtered.filter(r => ticked.has(r.item_id));
  const tickedAll     = rows.filter(r => ticked.has(r.item_id));
  const exportRows    = tickedAll.length > 0 ? tickedAll : filtered;
  const allVisibleTicked = filtered.length > 0 && filtered.every(r => ticked.has(r.item_id));
  const toggleAllVisible = () => {
    setTicked(prev => {
      const next = new Set(prev);
      if (allVisibleTicked) filtered.forEach(r => next.delete(r.item_id));
      else filtered.forEach(r => next.add(r.item_id));
      return next;
    });
  };
  const toggleOne = (id: string) => {
    setTicked(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  const scopeNote = (tickedAll.length > 0 ? [
    `${tickedAll.length} item${tickedAll.length === 1 ? "" : "s"} selected by hand`,
  ] : [
    project ? `Project crew: ${project.project_code} ${project.project_name}` : null,
    scope === "mine" ? "My allocation" : null,
    kitFilter !== "all" ? `Kit: ${kitFilter === "__pool__" ? "Pool" : kitFilter}` : null,
    personFilter !== "all" ? `Allocated to: ${personFilter === "__unassigned__" ? "Unassigned" : personFilter}` : null,
    colourFilter !== "all" ? `Tag colour: ${colourFilter}` : null,
    urgencyFilter !== "all" ? `Status: ${URGENCY_LABEL[urgencyFilter] ?? urgencyFilter}` : null,
    search.trim() ? `Search: "${search.trim()}"` : null,
  ]).filter(Boolean).join(" · ");

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto pb-16">
      <div className="mb-3 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Inspection register</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Every item and its latest inspection. Search by serial number, code or kit — tap a row for the full record.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" icon={Download} onClick={() => setExportOpen(true)} disabled={exportRows.length === 0}>
            Export{tickedAll.length > 0 ? ` (${tickedAll.length})` : ` (${filtered.length})`}
          </Button>
          {!isKiosk && (
            <>
              <Link to={addHref}>
                <Button variant="secondary" icon={PackagePlus}>Add equipment</Button>
              </Link>
              <Link to={`/inspections/new?kind=${tab}`}>
                <Button variant="primary" icon={Plus}>Log inspection</Button>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Domain tabs — Rigging / Test & Tag / Plant (coming soon).
          One register per domain so audit + field UX stays focused. */}
      <div className="mb-5 flex items-center gap-1 border-b border-line">
        <DomainTab
          label="Rigging Equipment"
          icon={HardHat}
          active={tab === "rigging"}
          onClick={() => setTab("rigging")}
        />
        <DomainTab
          label="Test & Tag"
          icon={Zap}
          active={tab === "power_tool"}
          onClick={() => setTab("power_tool")}
        />
        <DomainTab
          label="Plant"
          icon={Wrench}
          comingSoon
        />
      </div>

      {/* Summary strip — the whole health check in five numbers */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <MiniStat label="Total"            value={counts.total}/>
        <MiniStat label="Overdue"          value={counts.overdue}        tone={counts.overdue        > 0 ? "danger"  : undefined}/>
        <MiniStat label="Due soon"         value={counts.dueSoon}        tone={counts.dueSoon        > 0 ? "warning" : undefined}/>
        <MiniStat label="Never inspected"  value={counts.neverInspected} tone={counts.neverInspected > 0 ? "warning" : undefined}/>
        <MiniStat label="Current"          value={counts.current}        tone="ok"/>
      </div>

      {/* Filter strip */}
      <Card className="mb-4">
        <div className="p-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 px-2 py-1.5 bg-ink-raised border border-line rounded-md flex-1 min-w-[200px] max-w-md">
            <Search size={13} className="text-text-muted"/>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search S/N, code, kit, person, tag…"
              className="bg-transparent outline-none text-text text-[13px] flex-1"
            />
          </div>
          {canScope && (
            <>
              <FilterPill label="All equipment" active={scope === "all"}  onClick={() => setScope("all")}/>
              <FilterPill label="Mine"          active={scope === "mine"} onClick={() => setScope("mine")}/>
              <span className="text-text-dim text-[11px] mx-1">·</span>
            </>
          )}
          <Filter size={12} className="text-text-muted ml-1"/>
          <FilterPill label="All status"      active={urgencyFilter === "all"}             onClick={() => setUrgencyFilter("all")}/>
          <FilterPill label="Overdue"         active={urgencyFilter === "overdue"}         onClick={() => setUrgencyFilter("overdue")} tone="danger"/>
          <FilterPill label="Due soon"        active={urgencyFilter === "due_soon"}        onClick={() => setUrgencyFilter("due_soon")} tone="warn"/>
          <FilterPill label="Never inspected" active={urgencyFilter === "never_inspected"} onClick={() => setUrgencyFilter("never_inspected")} tone="warn"/>
          <FilterPill label="Current"         active={urgencyFilter === "current"}         onClick={() => setUrgencyFilter("current")}/>
        </div>
        <div className="px-3 pb-3 flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">Project crew</span>
          <select
            value={projectId}
            onChange={e => setProjectId(e.target.value)}
            className="bg-ink-raised border border-line rounded px-2 py-1 text-[11px] text-text outline-none max-w-[320px]"
          >
            <option value="">Any — whole company</option>
            {projects.map(p => (
              <option key={p.project_id} value={p.project_id}>{p.project_code} · {p.project_name}</option>
            ))}
          </select>
          {projectId && (
            <span className="text-[11px] text-text-muted">
              {crewIds ? `${crewIds.size} on the roster → gear allocated to them` : "loading roster…"}
            </span>
          )}
        </div>
        {(kits.length > 0 || people.length > 0) && (
          <div className="px-3 pb-3 flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">Kit</span>
            <FilterPill label="All" active={kitFilter === "all"} onClick={() => setKitFilter("all")} small/>
            {kits.map(k => (
              <FilterPill key={k} label={k} active={kitFilter === k} onClick={() => setKitFilter(k)} small/>
            ))}
            <FilterPill label="Pool" active={kitFilter === "__pool__"} onClick={() => setKitFilter("__pool__")} small/>
            <span className="text-text-dim text-[11px] mx-1">·</span>
            <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">Allocated to</span>
            <select
              value={personFilter}
              onChange={e => setPersonFilter(e.target.value)}
              className="bg-ink-raised border border-line rounded px-2 py-0.5 text-[11px] text-text outline-none max-w-[220px]"
            >
              <option value="all">Anyone</option>
              <option value="__unassigned__">Unassigned</option>
              {people.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <span className="text-text-dim text-[11px] mx-1">·</span>
            <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">Colour</span>
            {(["all", "Red", "Green", "Blue", "Yellow"] as const).map(c => (
              <FilterPill
                key={c}
                label={c === "all" ? "All" : c}
                active={colourFilter === c}
                onClick={() => setColourFilter(c)}
                small
              />
            ))}
          </div>
        )}
      </Card>

      {/* Tick toolbar — filter down, tick all shown, repeat; ticks survive filter changes */}
      {!isLoading && filtered.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-2 text-[12px] text-text-muted">
          <button type="button" onClick={toggleAllVisible} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-line hover:border-accent/40 hover:text-text">
            {allVisibleTicked ? <CheckSquare size={13}/> : <Square size={13}/>}
            {allVisibleTicked ? `Untick the ${filtered.length} shown` : `Tick the ${filtered.length} shown`}
          </button>
          {ticked.size > 0 && (
            <>
              <span><span className="text-accent font-medium">{ticked.size}</span> ticked for export{tickedVisible.length !== ticked.size ? ` (${ticked.size - tickedVisible.length} hidden by the filter)` : ""}</span>
              <button type="button" onClick={() => setTicked(new Set())} className="px-2.5 py-1 rounded border border-line hover:border-accent/40 hover:text-text">Clear ticks</button>
            </>
          )}
          {ticked.size === 0 && <span className="text-text-dim">Nothing ticked — Export takes everything shown. Tick rows to export just those.</span>}
        </div>
      )}

      {/* Body */}
      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner/></div>
      ) : filtered.length === 0 ? (
        <Card><div className="p-6"><EmptyState
          title={
            rows.length === 0 ? "No equipment in the register yet"
            : scoped.length === 0 && projectId ? "Nothing allocated to this project's crew"
            : scoped.length === 0 ? "Nothing allocated to you"
            : "No matches for this filter"
          }
          description={
            rows.length === 0 ? "Use Add equipment to register the first item."
            : scoped.length === 0 && projectId ? "The export can only follow allocation. Check who holds the gear on HR → Assets, or tick rows by hand instead."
            : scoped.length === 0 ? "Switch to All equipment to look up anything on site."
            : "Try clearing a filter, or search by the serial number on the item."
          }
          icon={ShieldCheck}
        /></div></Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="border-b border-line">
                <th className="px-3 py-3 w-8">
                  <button type="button" onClick={toggleAllVisible} title={allVisibleTicked ? "Untick all shown" : "Tick all shown"} className="text-text-muted hover:text-text">
                    {allVisibleTicked ? <CheckSquare size={14}/> : <Square size={14}/>}
                  </button>
                </th>
                {["Code", "Type", "Kit / class", "Allocated to", "Tag", "Last", "Next", "Status", ""].map(h => (
                  <th key={h} className="text-left px-3 py-3 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {filtered.map((r, i) => (
                  <RegisterRowEl
                    key={`${r.kind}-${r.item_id}`}
                    row={r}
                    alt={i % 2 === 1}
                    ticked={ticked.has(r.item_id)}
                    onTick={() => toggleOne(r.item_id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {exportOpen && (
        <ExportDialog
          rows={exportRows}
          domainLabel={tab === "rigging" ? "Rigging equipment" : "Test & tag"}
          defaultSite={project ? `${project.project_code} ${project.project_name}` : ""}
          scopeNote={scopeNote}
          preparedByName={[user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.login_email || "Coretel Ops"}
          onClose={() => setExportOpen(false)}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

function localDayKey(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function ExportDialog({ rows, domainLabel, defaultSite, scopeNote, preparedByName, onClose }: {
  rows: RegisterRow[];
  domainLabel: string;
  defaultSite: string;
  scopeNote: string;
  preparedByName: string;
  onClose: () => void;
}) {
  const [preparedFor, setPreparedFor]       = useState("");
  const [siteLabel, setSiteLabel]           = useState(defaultSite);
  const [includeHistory, setIncludeHistory] = useState(true);
  const [busy, setBusy]                     = useState<"xlsx" | "pdf" | null>(null);
  const [err, setErr]                       = useState<string | null>(null);

  const run = async (format: "xlsx" | "pdf") => {
    setBusy(format); setErr(null);
    try {
      const mod = await import("./inspectionRegisterExport");
      const opts = { rows, domainLabel, preparedFor, siteLabel, scopeNote, includeHistory, preparedByName };
      if (format === "xlsx") await mod.exportRegisterXlsx(opts);
      else await mod.exportRegisterPdf(opts);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Export failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[15px] font-semibold flex items-center gap-2"><Download size={15} className="text-accent"/> Export register</div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text p-2.5 -m-2.5 rounded-md"><X size={16}/></button>
        </div>
        <p className="text-[12px] text-text-muted mb-3">
          {rows.length} item{rows.length === 1 ? "" : "s"} · {domainLabel}
          {scopeNote && <span className="block text-[11px] text-text-dim mt-0.5">{scopeNote}</span>}
        </p>

        <Field label="Prepared for" hint="Client or person asking, as it should print. Leave blank for an internal copy.">
          <Input value={preparedFor} onChange={e => setPreparedFor(e.target.value)} placeholder="e.g. the client's field manager"/>
        </Field>
        <Field label="Site / job">
          <Input value={siteLabel} onChange={e => setSiteLabel(e.target.value)} placeholder="e.g. Mount Tamborine NBN"/>
        </Field>
        <label className="flex items-center gap-2 text-[12px] text-text mb-4 cursor-pointer select-none">
          <input type="checkbox" checked={includeHistory} onChange={e => setIncludeHistory(e.target.checked)} className="accent-accent"/>
          Include every inspection on record for these items (second sheet / second section)
        </label>

        {err && <div className="mb-3 px-3 py-2 rounded bg-danger/10 border border-danger/30 text-danger text-[12px]">{err}</div>}

        <div className="flex gap-2">
          <Button variant="secondary" icon={FileSpreadsheet} onClick={() => run("xlsx")} disabled={!!busy} className="flex-1 justify-center">
            {busy === "xlsx" ? "Building…" : "Excel"}
          </Button>
          <Button variant="primary" icon={FileText} onClick={() => run("pdf")} disabled={!!busy} className="flex-1 justify-center">
            {busy === "pdf" ? "Building…" : "PDF"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RegisterRowEl({ row, alt, ticked, onTick }: { row: RegisterRow; alt: boolean; ticked: boolean; onTick: () => void }) {
  const isRig = row.kind === "rigging";
  const tone  = URGENCY_TONE[row.urgency ?? "default"] ?? "default";
  const href  = `/inspections/equipment/${row.kind}/${row.item_id}`;
  return (
    <tr className={`border-b border-line cursor-pointer hover:bg-ink-raised/40 transition-colors ${alt ? "bg-ink/40" : ""} ${ticked ? "bg-accent/5" : ""}`}>
      <td className="px-3 py-3 w-8">
        <button type="button" onClick={onTick} className={ticked ? "text-accent" : "text-text-dim hover:text-text"} title={ticked ? "Untick" : "Tick for export"}>
          {ticked ? <CheckSquare size={14}/> : <Square size={14}/>}
        </button>
      </td>
      <td className="px-3 py-3">
        <Link to={href} className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded ${isRig ? "bg-accent/12 text-accent" : "bg-warn/12 text-warn"} flex items-center justify-center shrink-0`}>
            {isRig ? <HardHat size={11}/> : <Wrench size={11}/>}
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{row.display_name ?? row.code}</div>
            <div className="text-[10px] text-text-muted font-mono">
              {row.code}
              {row.serial_number && <> · S/N {row.serial_number}</>}
            </div>
          </div>
        </Link>
      </td>
      <td className="px-3 py-3 text-[12px] text-text-muted">{isRig ? "Rigging" : "Tool"}</td>
      <td className="px-3 py-3 text-[12px] text-text-muted">
        {row.kit_name ?? <span className="text-text-dim">Pool</span>}
        {row.item_class && <span className="block text-[11px] text-text-dim">{row.item_class}</span>}
        {(row.make || row.model) && (
          <span className="block text-[11px] text-text-dim truncate">
            {[row.make, row.model].filter(Boolean).join(" ")}
          </span>
        )}
      </td>
      <td className="px-3 py-3 text-[12px] text-text">{row.assigned_to_name ?? <span className="text-text-dim">Unassigned</span>}</td>
      <td className="px-3 py-3 text-[12px] font-mono">
        {row.tag_colour ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tagSwatch(row.tag_colour) }}/>
            {row.tag_colour}{row.tag_number ? ` · ${row.tag_number}` : ""}
          </span>
        ) : <span className="text-text-dim">—</span>}
      </td>
      <td className="px-3 py-3 text-[12px] text-text-muted">{row.last_inspection_date ?? "—"}</td>
      <td className="px-3 py-3 text-[12px] text-text-muted">{row.next_inspection_date ?? "—"}</td>
      <td className="px-3 py-3"><Pill label={URGENCY_LABEL[row.urgency ?? ""] ?? (row.urgency ?? "—")} small tone={tone as any}/></td>
      <td className="px-3 py-3">
        <Link to={href}>
          <ChevronRight size={14} className="text-text-dim"/>
        </Link>
      </td>
    </tr>
  );
}

function tagSwatch(colour: string): string {
  switch (colour) {
    case "Red":    return "#dc2626";
    case "Green":  return "#16a34a";
    case "Blue":   return "#2563eb";
    case "Yellow": return "#facc15";
    default:       return "#9ca3af";
  }
}

function DomainTab({
  label, icon: Icon, active, onClick, comingSoon,
}: {
  label:        string;
  icon:         typeof HardHat;
  active?:      boolean;
  onClick?:     () => void;
  comingSoon?:  boolean;
}) {
  if (comingSoon) {
    return (
      <div
        className="px-4 py-2.5 text-[13px] inline-flex items-center gap-2 text-text-dim cursor-not-allowed"
        title="Coming soon — pre-use checks for Hiab and other plant"
      >
        <Icon size={14}/>
        <span>{label}</span>
        <span className="text-[9px] font-mono uppercase tracking-wider2 px-1.5 py-0.5 bg-ink-raised border border-line rounded">soon</span>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2.5 text-[13px] inline-flex items-center gap-2 transition-colors ${
        active
          ? "text-accent border-b-2 border-accent -mb-px font-medium"
          : "text-text-muted hover:text-text"
      }`}
    >
      <Icon size={14}/>
      <span>{label}</span>
    </button>
  );
}

function FilterPill({ label, active, onClick, tone, small }: {
  label: string; active: boolean; onClick: () => void; tone?: "danger" | "warn"; small?: boolean;
}) {
  const activeClass = tone === "danger" ? "bg-danger/15 text-danger border-danger/40"
                    : tone === "warn"   ? "bg-warn/15 text-warn border-warn/40"
                    :                     "bg-accent/15 text-accent border-accent/40";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${small ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]"} rounded border transition-colors ${
        active ? activeClass : "border-line text-text-muted hover:text-text hover:border-accent/40"
      }`}
    >
      {label}
    </button>
  );
}

function MiniStat({ label, value, tone }: { label: string; value: number; tone?: "danger" | "warning" | "ok" }) {
  const cls = tone === "danger" ? "text-danger" : tone === "warning" ? "text-warn" : tone === "ok" ? "text-ok" : "text-text";
  return (
    <Card>
      <div className="p-3">
        <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">{label}</div>
        <div className={`text-xl font-semibold mt-0.5 ${cls}`}>{value}</div>
      </div>
    </Card>
  );
}
